import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleBySlug, getRecentArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = getRecentArticles(4).filter((a) => a.slug !== slug).slice(0, 3);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您查找的文章不存在或已被移除。</p>
            <Link to="/" className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: article.categoryName, path: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs font-medium text-primary-500 bg-primary-50 px-3 py-1 rounded-full cursor-pointer"
                >
                  {article.categoryName}
                </Link>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs text-foreground-400 bg-background-100 px-3 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed
                [&>h3]:font-heading [&>h3]:text-xl [&>h3]:font-bold [&>h3]:text-foreground-900 [&>h3]:mt-8 [&>h3]:mb-4
                [&>p]:text-foreground-700 [&>p]:mb-5 [&>p]:leading-loose [&>p]:text-base
                [&>strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="border-t border-background-200/70 mt-10 pt-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-500">分享到：</span>
                  <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link"></i>
                  </button>
                </div>
                <Link
                  to={`/category/${article.category}`}
                  className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer"
                >
                  查看更多{article.categoryName}资讯 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </article>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">相关文章</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((related) => (
                <Link
                  key={related.slug}
                  to={`/article/${related.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-200 cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={related.image}
                      alt={related.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 object-top"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-xs text-foreground-400">{related.publishDate}</span>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mt-1">
                      {related.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Organization',
              name: article.author,
            },
            datePublished: article.publishDate,
            url: `https://www.bbtiyu.com/article/${slug}`,
            publisher: {
              '@type': 'Organization',
              name: 'BB体育',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.bbtiyu.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.categoryName,
                item: `https://www.bbtiyu.com/category/${article.category}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
    </>
  );
}